import "./Home.scss";
import React, { useEffect, useState } from "react";

import { Helmet } from "react-helmet";
import { Container } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query/react";

import ArticleCards from "../../organisms/Cards";
import { SectionHeader, SectionHeading, SectionIntro } from "../../atoms/SectionHeader";
import { fetch_personalised_content } from "../../../store/actions/getPersonalisedContent";
import { update_current_tab_index } from "../../../store/actions/updateCurrentViewData";
import { CircularLoader } from "../../atoms/Loaders";
import { greyVelvet } from "../../../assets/img/bg";
import NotificationBar from "../../atoms/NotificationBar/NotificationBar";
import CurrentStepCard from "../../molecules/CurrentStepCard";
import NotStartedProgrammeCard from "../../molecules/NotStartedProgrammeCard";
import PageBackground from "../../organisms/PageBackground";
import HeroBanner from "../../organisms/HeroBanner";
import MiddleBanner from "../../organisms/MiddleBanner";
import useUser from "../../hooks/useUser";
import { Programmes, smokeFreeCardData, loseWeightCardData } from "../../../data/Programme";
import { Tabs } from "@thrivetribe/gloji.ui.molecules.tabs";
import { useGetProfileByIdQuery } from "../../../redux/profileApiV3";
import { useGetProgrammeOverviewsQuery } from "../../../redux/programmeApiV2";
import useUserId from "../../hooks/useUserId";
import { getJourneyAndProgrammeIds, getProgrammesByProgrammeIds } from "../../../utils/JourneyUtils";
import ProgrammeCard from "../../molecules/ProgrammeCard";

import useFeatureFlag from "../../hooks/useFeatureFlag";
import Features from "../../../enums/features";
import { featureService } from "../../services";
import SfOverviewImg from "../../../assets/img/sf-overview.png";
import LwOverviewImg from "../../../assets/img/lw-overview.png";

/**
 * A page component which renders the home page
 */
function Home() {
  const [publicTags, setPublicTags] = useState();
  const [overviewsRequest, setOverviewsRequest] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [programmeCardData, setProgrammeCardData] = useState();

  const [user] = useUser();
  const [userId] = useUserId();
  const dispatch = useDispatch();
  const content = useSelector(state => state.personalisedContent);
  const step = useSelector(state => state.programmeStep);

  const [configMultiJourneyEnabled, configMultiJourneyReady] = useFeatureFlag(
    Features.MULTIPLE_MY_PATH,
    featureService.isFeatureEnabled,
    true
  );

  const { data: profile, isSuccess: profileIsSuccess } = useGetProfileByIdQuery(userId ?? skipToken);
  const {
    data: programmeOverviews,
    isSuccess: overviewsIsSuccess,
    isUninitialized: overviewsIsUninitialized,
  } = useGetProgrammeOverviewsQuery(overviewsRequest ?? skipToken);

  useEffect(() => {
    if (!programmeOverviews) {
      return;
    }

    let data = programmeOverviews.map(x => {
      return {
        programmeId: x.programme.id,
        primaryButtonUrl: `/my-path/${x.programme?.slug}/step-${x.nextAction?.stepShortTitle}`,
        secondaryButtonUrl: `/my-path/${x.programme?.slug}`,
        primaryButtonText: "Let's get started",
        programmeTitle: x.programme.title,
        cardTitle: x.nextAction?.partTitle,
        description: x.nextAction?.partWhatIsCovered,
        imgUrl:
          x.nextAction?.video?.thumbnail?.file?.url ??
          `https://image.mux.com/${x.nextAction?.video?.videoDetails?.playbackId}/thumbnail.jpg?width=450&time=9`,

        imgTitle: x.nextAction?.video?.videoDetails?.title,
        enablePlayIcon: true,
      };
    });

    if (!programmeOverviews.some(x => x.programme.id === smokeFreeCardData.programmeId)) {
      data = [...data, smokeFreeCardData];
    }

    if (!programmeOverviews.some(x => x.programme.id === loseWeightCardData.programmeId)) {
      data = [...data, loseWeightCardData];
    }

    setProgrammeCardData(data);
  }, [programmeOverviews]);

  useEffect(() => {
    if (!profileIsSuccess) {
      return;
    }

    if (profile.currentJourneys && profile.currentJourneys.length > 0) {
      setOverviewsRequest({
        profileId: userId,
        programmeAndJourneyIds: getJourneyAndProgrammeIds(profile),
      });
    }
  }, [profile, profileIsSuccess, userId]);

  useEffect(() => {
    if (!profileIsSuccess || !overviewsIsSuccess || overviewsIsUninitialized) {
      return;
    }

    if (!programmeOverviews) {
      return;
    }

    let tabData = [];
    programmeOverviews?.forEach(x => {
      tabData = [
        ...tabData,
        { title: x.programme.title, children: <CurrentStepCard programmeOverview={x} /> },
      ];
    });

    if (!programmeOverviews?.some(x => x.programme.id === smokeFreeCardData.programmeId)) {
      tabData = [
        ...tabData,
        {
          title: smokeFreeCardData.programmeTitle,
          children: <NotStartedProgrammeCard imgUrl={SfOverviewImg} programmeOverview={smokeFreeCardData} />,
        },
      ];
    }

    if (!programmeOverviews?.some(x => x.programme.id === loseWeightCardData.programmeId)) {
      tabData = [
        ...tabData,
        {
          title: loseWeightCardData.programmeTitle,
          children: <NotStartedProgrammeCard imgUrl={LwOverviewImg} programmeOverview={loseWeightCardData} />,
        },
      ];
    }

    setTabs(tabData);
  }, [programmeOverviews]);

  useEffect(() => {
    const userPastAndCurrentProgrammes = getProgrammesByProgrammeIds(profile.pastAndCurrentProgrammeIds);

    const tags = userPastAndCurrentProgrammes.map(
      x => Programmes.find(item => item.programmeId === x.programmeId)?.tag
    );
    setPublicTags(tags);
  }, [programmeOverviews, profile.pastAndCurrentProgrammeIds, overviewsIsSuccess]);

  useEffect(() => {
    if (userId && publicTags) {
      dispatch(fetch_personalised_content(userId, publicTags));
    }
  }, [dispatch, userId, publicTags]);

  useEffect(() => {
    dispatch(update_current_tab_index(step?.data?.currentPosition?.currentPartNumber - 1));
  }, [dispatch, step?.data]);

  return (
    <PageBackground bgImage={greyVelvet}>
      <Helmet>
        <title>Home | Gloji</title>
      </Helmet>
      {content && content.loading === false ? (
        <>
          <NotificationBar />
          <HeroBanner>
            <h1 className="hero-banner__heading">
              {user && user.given_name ? `Hi ${user.given_name}, welcome to gloji` : "Welcome to gloji"}
            </h1>
            {configMultiJourneyReady && configMultiJourneyEnabled ? (
              <Tabs tabs={tabs} />
            ) : (
              !!programmeOverviews && <CurrentStepCard programmeOverview={programmeOverviews[0]} />
            )}
          </HeroBanner>
          <Container className="main-content__container diagonal-banner__top-neighbour">
            <section>
              <SectionHeader>
                <SectionHeading> Just for you...</SectionHeading>
                <SectionIntro>
                  We've picked these tips, guides and recipes to help you succeed on your journey with gloji.
                </SectionIntro>
              </SectionHeader>
              <ArticleCards data={content.data.personalisedContent} />
            </section>
          </Container>
          {configMultiJourneyReady && configMultiJourneyEnabled && (
            <Container className="main-content__container diagonal-banner__top-neighbour programme-cards">
              <section>
                <SectionHeader>
                  <SectionHeading> Change your life with gloji</SectionHeading>
                  <SectionIntro>
                    We’ve got a range of programmes that will help you take your health and wellbeing to the
                    next level!
                  </SectionIntro>
                </SectionHeader>
                {!!programmeOverviews && (
                  <>
                    <div className="programme-cards__container">
                      {programmeCardData &&
                        programmeCardData.map((x, index) => {
                          return (
                            <ProgrammeCard
                              key={x.programmeId}
                              primaryButtonUrl={x.primaryButtonUrl}
                              primaryButtonText={x.primaryButtonText}
                              secondaryButtonUrl={x.secondaryButtonUrl}
                              programmeTitle={x.programmeTitle}
                              cardTitle={x.cardTitle}
                              imgUrl={x.imgUrl}
                              imgTitle={x.imgTitle}
                              description={x.description}
                              enablePlayIcon={x.enablePlayIcon}
                            />
                          );
                        })}
                    </div>
                  </>
                )}
              </section>
            </Container>
          )}

          <MiddleBanner />
          <Container className="main-content__container diagonal-banner__bottom-neighbour">
            <section>
              <SectionHeader>
                <SectionHeading>Got a little more time? </SectionHeading>
                <SectionIntro>Tap into our hive mind and boost your chances of success.</SectionIntro>
              </SectionHeader>
              <ArticleCards data={content.data.nonPersonalisedContent} />
            </section>
          </Container>
        </>
      ) : (
        <CircularLoader color="primary" size={40} />
      )}
    </PageBackground>
  );
}

export default Home;
